import { CheckCircleIcon, ClockIcon, DocumentTextIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useActionData, useLoaderData, useNavigation } from '@remix-run/react';
import { DataFunctionArgs, json, redirect } from '@remix-run/server-runtime';
import { withZod } from '@remix-validated-form/with-zod';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ValidatedForm, validationError } from 'remix-validated-form';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { ErrorMessage } from '~/components/ErrorMessage';
import { HighlightedButton } from '~/components/HighlightedButton';
import { Input } from '~/components/Input';
import { Select } from '~/components/Select';
import { SuccessMessage } from '~/components/SuccessMessage';
import { API_URL } from '~/constants';
import { getActiveCustomer, updateActiveCustomer } from '~/providers/account/account';
import { getActiveCustomerDetails } from '~/providers/customer/customer';
import { getSessionStorage } from '~/sessions';

// File upload constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ALLOWED_DOCUMENT_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

// Business Types Enum
const BusinessType = {
  // Accommodation
  Hotel: 'Hotel',
  Resort: 'Resort',
  Guesthouse: 'Guesthouse',
  Hostel: 'Hostel',
  // Restaurants
  FineDining: 'FineDining',
  Casual: 'Casual',
  QuickService: 'QuickService',
  // Bars & Clubs
  BarPub: 'BarPub',
  ClubDisco: 'ClubDisco',
  // Cafes & Canteens
  Cafe: 'Cafe',
  Canteen: 'Canteen',
  // Alcohol
  Alcohol: 'Alcohol',
  Beer: 'Beer',
  Domestic: 'Domestic',
  Imported: 'Imported',
  Wine: 'Wine',
  AllVarious: 'AllVarious',
  // Suppliers
  KitchenEquipments: 'KitchenEquipments',
  ElectronicsSuppliers: 'ElectronicsSuppliers',
  // Services
  ServiceProviders: 'ServiceProviders',
  Contractor: 'Contractor',
  Electrician: 'Electrician',
  Plumber: 'Plumber',
  // Financial
  Bank: 'Bank',
  Insurance: 'Insurance',
} as const;

// KYC Status Enum - must match backend schema
export enum KYCStatus {
  NotVerified = 'NOT_VERIFIED',
  Rejected = 'REJECTED',
  BeingVerified = 'BEING_VERIFIED',
  Verified = 'VERIFIED',
}

// Government ID Types
const GovernmentIDType = {
  NationalID: 'NationalID',
  Passport: 'Passport',
  DriversLicense: 'DriversLicense',
} as const;

enum FormIntent {
  SubmitKYC = 'submitKYC',
  UpdateKYC = 'updateKYC',
}

// Type for file upload response from backend
type FileUploadResponse = {
  status: boolean;
  message?: string;
  customerId?: number;
  uploadedLinks?: Array<{
    field: string;
    name: string;
    url: string;
  }>;
  error?: string;
};

// Type for Asset from backend
type Asset = {
  id: string;
  name?: string;
  preview?: string;
  source?: string;
};

// Extended type for customer custom fields with file Assets
type CustomerCustomFieldsWithAssets = {
  businessName?: string | null;
  businessType?: string[] | null;
  registrationNumber?: string | null;
  physicalAddress?: string | null;
  yearsInOperation?: number | null;
  ownerFullName?: string | null;
  ownerEmail?: string | null;
  ownerPhoneNumber?: string | null;
  governmentIDType?: string | null;
  governmentIDNumber?: string | null;
  kycStatus?: string | null;
  governmentIDFile?: Asset | null;
  financialStatementFile?: Asset | null;
  salesRecordFile?: Asset | null;
  premisesPhoto1?: Asset | null;
  premisesPhoto2?: Asset | null;
  premisesPhoto3?: Asset | null;
};

// Helper function to upload all files in a single request via REST API
async function uploadFilesToVendure(
  files: Record<string, File | null>,
  customerId: string,
  request: Request,
): Promise<Record<string, number> | null> {
  try {
    const sessionStorage = await getSessionStorage();
    const session = await sessionStorage.getSession(request.headers.get('Cookie'));
    const token = session?.get('authToken');

    const formData = new FormData();
    let hasFiles = false;

    Object.entries(files).forEach(([fieldName, file]) => {
      if (file && file.size > 0) {
        formData.append(fieldName, file);
        hasFiles = true;
      }
    });

    if (!hasFiles) {
      return null;
    }

    const baseUrl = API_URL.replace('/shop-api', '');
    const uploadUrl = `${baseUrl}/file/upload/${customerId}`;
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        ...(token ? { 'Vendure-auth-token': token } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('[KYC Upload] HTTP error:', response.status, errorText);
      // User-friendly message - don't show technical details
      return null;
    }

    const result: FileUploadResponse = await response.json();

    if (!result.status) {
      console.error('[KYC Upload] Upload failed:', result.message || result.error);
      // User-friendly message will be shown in action handler
      return null;
    }

    if (result.uploadedLinks && result.uploadedLinks.length > 0) {
      const uploadedFields: Record<string, number> = {};
      result.uploadedLinks.forEach((link) => {
        uploadedFields[link.field] = 1;
      });
      // Success toast will be shown in action handler after all processing
      return uploadedFields;
    }

    return null;
  } catch (error) {
    console.error('[KYC Upload] Exception during file upload:', error);
    // User-friendly message will be shown in action handler
    return null;
  }
}

// Validation Schema
export const kycValidator = withZod(
  z.object({
    businessName: z.string().min(1, { message: 'Business name is required' }),
    businessType: z.string().min(1, { message: 'At least one business type is required' }),
    registrationNumber: z
      .string()
      .min(1, { message: 'Registration number is required' })
      .min(3, { message: 'Registration number must be at least 3 characters' })
      .max(50, { message: 'Registration number must not exceed 50 characters' })
      .regex(/^[a-zA-Z0-9\-\/]+$/, {
        message: 'Registration number can only contain letters, numbers, hyphens, and forward slashes'
      }),
    physicalAddress: z.string().min(1, { message: 'Physical address is required' }),
    yearsInOperation: z.string().min(1, { message: 'Years in operation is required' }),
    ownerFullName: z.string().min(1, { message: 'Owner full name is required' }),
    ownerEmail: z.string().email('Must be a valid email').optional().or(z.literal('')),
    ownerPhoneNumber: z
      .string()
      .min(1, { message: 'Owner phone number is required' })
      .regex(/^\+?[0-9]{10,15}$/, {
        message: 'Phone number must be 10-15 digits and can optionally start with +'
      }),
    governmentIDType: z.string().min(1, { message: 'ID type is required' }),
    governmentIDNumber: z.string().min(1, { message: 'ID number is required' }),
  }),
);

// Loader - Get current KYC data if exists
export async function loader({ request }: DataFunctionArgs) {
  const { activeCustomer } = await getActiveCustomerDetails({ request });
  if (!activeCustomer) {
    return redirect('/sign-in');
  }

  // Fetch KYC data from customer customFields
  const customerWithKYC = await getActiveCustomer({ request });

  // Map customFields to KYC data structure
  const customFields = customerWithKYC?.customFields as CustomerCustomFieldsWithAssets | undefined;
  const kycData = {
    status: (customFields?.kycStatus || KYCStatus.NotVerified) as KYCStatus,
    rejectionReason: null,
    // Form data with string values for form inputs
    data: customFields ? {
      businessName: customFields.businessName || '',
      businessType: customFields.businessType?.[0] || '', // Take first type for form select
      registrationNumber: customFields.registrationNumber || '',
      physicalAddress: customFields.physicalAddress || '',
      yearsInOperation: customFields.yearsInOperation?.toString() || '', // Convert to string for form
      ownerFullName: customFields.ownerFullName || '',
      ownerEmail: customFields.ownerEmail || '',
      ownerPhoneNumber: customFields.ownerPhoneNumber || '',
      governmentIDType: customFields.governmentIDType || '',
      governmentIDNumber: customFields.governmentIDNumber || '',
    } : null,
  };

  return json({ activeCustomer, kycData, customFields });
}

type FormError = {
  message: string;
};

type KYCSubmitResponse = {
  success: boolean;
  message: string;
};

// Type guards
function isKYCSubmitResponse(data: unknown): data is KYCSubmitResponse {
  return data !== null && typeof data === 'object' && 'success' in data && (data as KYCSubmitResponse).success === true && 'message' in data;
}

function isFormError(data: unknown): data is FormError {
  return data !== null && typeof data === 'object' && 'message' in data && !('success' in data);
}

// Action - Handle KYC form submission
export async function action({ request }: DataFunctionArgs) {
  const body = await request.formData();
  const intent = body.get('intent') as FormIntent | null;

  const formError = (formError: FormError, init?: number | ResponseInit) => {
    return json<FormError>(formError, init);
  };

  if (intent === FormIntent.SubmitKYC || intent === FormIntent.UpdateKYC) {
    const result = await kycValidator.validate(body);

    if (result.error) {
      return validationError(result.error);
    }

    const { data } = result;

    try {
      // Get customer ID for file uploads
      const { activeCustomer } = await getActiveCustomerDetails({ request });
      if (!activeCustomer) {
        return formError({ message: 'User not authenticated' }, { status: 401 });
      }

      // Check current KYC status before updating
      const currentCustomer = await getActiveCustomer({ request });
      const isUpdate = currentCustomer?.customFields?.kycStatus === KYCStatus.BeingVerified ||
        currentCustomer?.customFields?.kycStatus === KYCStatus.Rejected;

      const governmentIDFile = body.get('governmentIDFile') as File | null;
      const financialStatementFile = body.get('financialStatementFile') as File | null;
      const salesRecordFile = body.get('salesRecordFile') as File | null;
      const premisesPhoto1 = body.get('premisesPhoto1') as File | null;
      const premisesPhoto2 = body.get('premisesPhoto2') as File | null;
      const premisesPhoto3 = body.get('premisesPhoto3') as File | null;

      // Validate required files for initial submission
      if (intent === FormIntent.SubmitKYC && !isUpdate) {
        const requiredFiles = {
          governmentIDFile,
          financialStatementFile,
          salesRecordFile,
          premisesPhoto1,
          premisesPhoto2,
        };

        const missingFiles: string[] = [];
        Object.entries(requiredFiles).forEach(([fieldName, file]) => {
          const hasExistingFile = currentCustomer?.customFields?.[fieldName as keyof typeof currentCustomer.customFields];
          if ((!file || file.size === 0) && !hasExistingFile) {
            missingFiles.push(fieldName.replace(/([A-Z])/g, ' $1').trim());
          }
        });

        if (missingFiles.length > 0) {
          return formError({
            message: `Please upload all required documents: ${missingFiles.join(', ')}`
          }, { status: 400 });
        }
      }

      const filesToUpload = {
        governmentIDFile,
        financialStatementFile,
        salesRecordFile,
        premisesPhoto1,
        premisesPhoto2,
        premisesPhoto3,
      };

      const uploadedAssets = await uploadFilesToVendure(filesToUpload, activeCustomer.id, request);

      const hasFilesToUpload = Object.values(filesToUpload).some(file => file && file.size > 0);
      if (uploadedAssets === null && hasFilesToUpload) {
        console.error('[KYC] File upload failed for customer:', activeCustomer.id);
        return formError(
          { message: 'Failed to upload files. Please check your internet connection and try again. If the problem persists, contact support.' },
          { status: 500 }
        );
      }

      const response = await updateActiveCustomer(
        {
          customFields: {
            businessName: data.businessName,
            businessType: [data.businessType],
            registrationNumber: data.registrationNumber,
            physicalAddress: data.physicalAddress,
            yearsInOperation: parseInt(data.yearsInOperation, 10),
            ownerFullName: data.ownerFullName,
            ownerEmail: data.ownerEmail || null,
            ownerPhoneNumber: data.ownerPhoneNumber,
            governmentIDType: data.governmentIDType,
            governmentIDNumber: data.governmentIDNumber,
            kycStatus: KYCStatus.BeingVerified
          },
        },
        { request }
      );

      if (response) {
        return json<KYCSubmitResponse>({
          success: true,
          message: isUpdate
            ? 'KYC updated successfully. Your information is being verified.'
            : 'KYC submitted successfully and is being verified',
        });
      }

      console.error('[KYC] Failed to update customer:', activeCustomer.id);
      return formError({ message: 'Failed to process KYC request. Please try again or contact support.' }, { status: 500 });
    } catch (error) {
      console.error('[KYC] Unexpected error during submission:', error);
      return formError(
        { message: 'An unexpected error occurred. Please try again later or contact support if the issue persists.' },
        { status: 500 }
      );
    }
  }

  return formError({ message: 'Invalid form intent' }, { status: 400 });
}

export default function KYCForm() {
  const { activeCustomer, kycData, customFields } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const { state } = useNavigation();
  const { t } = useTranslation();

  const [formError, setFormError] = useState<FormError | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fileValidationErrors, setFileValidationErrors] = useState<Record<string, string>>({});
  const [filePreviews, setFilePreviews] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, boolean>>({});
  const [requiredFilesError, setRequiredFilesError] = useState<string | null>(null);

  // Track all created object URLs for cleanup
  const createdUrlsRef = useRef<Set<string>>(new Set());

  const isEditable = kycData.status !== KYCStatus.Verified; // Allow editing unless verified
  const isBeingVerified = kycData.status === KYCStatus.BeingVerified;
  const isVerified = kycData.status === KYCStatus.Verified;
  const isRejected = kycData.status === KYCStatus.Rejected;
  const existingFiles = customFields; // Use customFields directly for file asset checking
  const hasFileValidationErrors = Object.keys(fileValidationErrors).length > 0;

  // Check if all required files are present (either uploaded or existing)
  const hasAllRequiredFiles = (): boolean => {
    const requiredFileFields = ['governmentIDFile', 'financialStatementFile', 'salesRecordFile', 'premisesPhoto1', 'premisesPhoto2'];
    return requiredFileFields.every(field => {
      const hasNewFile = uploadedFiles[field];
      const hasExistingFile = existingFiles?.[field as keyof typeof existingFiles];
      return hasNewFile || hasExistingFile;
    });
  };

  // File validation helper
  const validateFile = (file: File | null, fieldName: string, isDocument: boolean = false): string | null => {
    if (!file || file.size === 0) return null;

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size must be less than ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB`;
    }

    // Check file type
    const allowedTypes = isDocument ? ALLOWED_DOCUMENT_TYPES : ALLOWED_IMAGE_TYPES;
    if (!allowedTypes.includes(file.type)) {
      return `Invalid file type. Allowed: ${isDocument ? 'Images or PDF' : 'Images only'}`;
    }

    return null;
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string, isDocument: boolean = false) => {
    const file = e.target.files?.[0] || null;
    const error = validateFile(file, fieldName, isDocument);

    setFileValidationErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[fieldName] = error;
        // Show user-friendly toast for file errors
        toast.error(error, {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        delete newErrors[fieldName];
      }
      return newErrors;
    });

    // Track uploaded files
    setUploadedFiles(prev => ({
      ...prev,
      [fieldName]: !!(file && file.size > 0 && !error)
    }));

    // Clear required files error when files are added
    if (file && file.size > 0 && !error) {
      setRequiredFilesError(null);
      // Show success toast for successful file selection
      const friendlyName = fieldName.replace(/([A-Z])/g, ' $1').trim();
      toast.success(`${friendlyName} selected successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });
    }

    // Generate preview for new file
    if (file && !error) {
      // Only create preview for images, not PDFs
      if (file.type.startsWith('image/')) {
        const previewUrl = URL.createObjectURL(file);
        createdUrlsRef.current.add(previewUrl);
        setFilePreviews(prev => {
          // Revoke old URL if it exists
          if (prev[fieldName]) {
            URL.revokeObjectURL(prev[fieldName]);
            createdUrlsRef.current.delete(prev[fieldName]);
          }
          return { ...prev, [fieldName]: previewUrl };
        });
      } else {
        // Remove preview for non-image files
        setFilePreviews(prev => {
          const newPreviews = { ...prev };
          delete newPreviews[fieldName];
          return newPreviews;
        });
      }
    } else if (!file) {
      // Remove preview if file is cleared
      setFilePreviews(prev => {
        if (prev[fieldName]) {
          URL.revokeObjectURL(prev[fieldName]);
          createdUrlsRef.current.delete(prev[fieldName]);
        }
        const newPreviews = { ...prev };
        delete newPreviews[fieldName];
        return newPreviews;
      });
    }
  };

  // Get preview URL (from new upload or existing backend asset)
  const getPreviewUrl = (fieldName: string, assetField?: Asset | null): string | null => {
    // Check for new file preview first
    if (filePreviews[fieldName]) {
      return filePreviews[fieldName];
    }
    // Fall back to existing asset preview/source
    if (assetField?.preview) {
      return assetField.preview;
    }
    if (assetField?.source) {
      return assetField.source;
    }
    return null;
  };

  // Cleanup all preview URLs on unmount
  useEffect(() => {
    return () => {
      // Revoke all object URLs created during the component lifecycle
      createdUrlsRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      createdUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (actionData) {
      if (isKYCSubmitResponse(actionData)) {
        setSuccessMessage(actionData.message);
        setFormError(null);
        toast.success(actionData.message, {
          position: "top-right",
          autoClose: 5000,
          icon: <span>🎉</span>,
        });
      } else if (isFormError(actionData)) {
        setFormError({ message: actionData.message });
        setSuccessMessage(null);
        toast.error(actionData.message, {
          position: "top-right",
          autoClose: 7000,
        });
      }
    }
  }, [actionData]);

  // Show uploading toast when form is submitting
  useEffect(() => {
    if (state === 'submitting') {
      toast.info('Uploading your documents... Please wait.', {
        position: "top-center",
        autoClose: false,
        toastId: 'uploading-toast',
        icon: <span>📤</span>,
      });
    } else {
      // Dismiss uploading toast when done
      toast.dismiss('uploading-toast');
    }
  }, [state]);

  const getStatusBadge = () => {
    switch (kycData.status) {
      case KYCStatus.NotVerified:
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <XCircleIcon className="w-5 h-5" />
            Not Verified
          </span>
        );
      case KYCStatus.Rejected:
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-5 h-5" />
            Rejected
          </span>
        );
      case KYCStatus.BeingVerified:
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-5 h-5" />
            Being Verified
          </span>
        );
      case KYCStatus.Verified:
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-5 h-5" />
            Verified
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 p-4 mt-5">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Merchant KYC</h2>
          <p className="text-sm text-gray-600 mt-1">
            Complete your business verification to unlock all features
          </p>
        </div>
        {getStatusBadge()}
      </div>

      {/* Rejection Message */}
      {kycData.rejectionReason && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-500" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                KYC Rejected
              </h3>
              <p className="mt-1 text-sm text-red-700">
                {kycData.rejectionReason}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <SuccessMessage heading="Success" message={successMessage} />
      )}



      {/* Submitting/Uploading Notice */}
      {state === 'submitting' && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Processing...
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                Uploading files and submitting your KYC information. Please do not close this page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rejected Notice */}
      {isRejected && !kycData.rejectionReason && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-500" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                KYC Rejected
              </h3>
              <p className="mt-1 text-sm text-red-700">
                Your KYC was rejected. Please update the required information and resubmit.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Being Verified Notice */}
      {isBeingVerified && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
          <div className="flex">
            <ClockIcon className="h-5 w-5 text-yellow-500" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Verification in Progress
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Your KYC is being verified by our team. You can still update any files or information if needed during this process.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Verified Notice */}
      {isVerified && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                KYC Verified
              </h3>
              <p className="mt-1 text-sm text-green-700">
                Your business has been verified successfully.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KYC Form */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <ValidatedForm
          validator={kycValidator}
          method="post"
          encType="multipart/form-data"
          defaultValues={kycData.data || undefined}
        >
          <input
            type="hidden"
            name="intent"
            value={isEditable ? FormIntent.SubmitKYC : FormIntent.UpdateKYC}
          />

          {/* Section 1: Business Details */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-primary" />
              Business Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Business Name"
                  name="businessName"
                  type="text"
                  disabled={!isEditable}
                  required
                  onKeyDown={(e) => {
                    // Allow navigation and editing keys
                    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', ' '];
                    if (allowedKeys.includes(e.key)) return;

                    // Allow only letters (a-z, A-Z)
                    if (!/^[a-zA-Z]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Strip out any numbers and special characters, keep only letters and spaces
                    const value = e.currentTarget.value;
                    e.currentTarget.value = value.replace(/[^a-zA-Z\s]/g, '');
                  }}
                />
              </div>
              <div>
                <Select
                  label="Business Type"
                  name="businessType"
                  disabled={!isEditable}
                  required
                >
                  <option value="">Select business type</option>
                  <optgroup label="Accommodation">
                    <option value={BusinessType.Hotel}>Hotel</option>
                    <option value={BusinessType.Resort}>Resort</option>
                    <option value={BusinessType.Guesthouse}>Guesthouse</option>
                    <option value={BusinessType.Hostel}>Hostel</option>
                  </optgroup>
                  <optgroup label="Restaurants">
                    <option value={BusinessType.FineDining}>Fine Dining</option>
                    <option value={BusinessType.Casual}>Casual</option>
                    <option value={BusinessType.QuickService}>Quick Service</option>
                  </optgroup>
                  <optgroup label="Bars & Clubs">
                    <option value={BusinessType.BarPub}>Bar/Pub</option>
                    <option value={BusinessType.ClubDisco}>Club/Disco</option>
                  </optgroup>
                  <optgroup label="Cafes & Canteens">
                    <option value={BusinessType.Cafe}>Cafe</option>
                    <option value={BusinessType.Canteen}>Canteen</option>
                  </optgroup>
                  <optgroup label="Alcohol">
                    <option value={BusinessType.Alcohol}>Alcohol</option>
                    <option value={BusinessType.Beer}>Beer</option>
                    <option value={BusinessType.Wine}>Wine</option>
                    <option value={BusinessType.AllVarious}>All Various</option>
                  </optgroup>
                  <optgroup label="Suppliers">
                    <option value={BusinessType.KitchenEquipments}>Kitchen Equipments</option>
                    <option value={BusinessType.ElectronicsSuppliers}>Electronics Suppliers</option>
                  </optgroup>
                  <optgroup label="Services">
                    <option value={BusinessType.ServiceProviders}>Service Providers</option>
                    <option value={BusinessType.Contractor}>Contractor</option>
                    <option value={BusinessType.Electrician}>Electrician</option>
                    <option value={BusinessType.Plumber}>Plumber</option>
                  </optgroup>
                  <optgroup label="Financial">
                    <option value={BusinessType.Bank}>Bank</option>
                    <option value={BusinessType.Insurance}>Insurance</option>
                  </optgroup>
                </Select>
              </div>
              <div>
                <Input
                  label="Registration Number"
                  name="registrationNumber"
                  disabled={!isEditable}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be 3-50 characters. Only letters, numbers, hyphens, and forward slashes allowed.
                </p>
              </div>
              <div className="md:col-span-2">
                <Input
                  label="Physical Address"
                  name="physicalAddress"
                  disabled={!isEditable}
                  required
                />
              </div>
              <div>
                <Input
                  label="Years in Operation"
                  name="yearsInOperation"
                  type="number"
                  disabled={!isEditable}
                  required
                />
              </div>
            </div>
          </div>

          {/* Section 2: Ownership Details */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-primary" />
              Ownership Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Owner Full Name"
                  name="ownerFullName"
                  type="text"
                  disabled={!isEditable}
                  required
                  onKeyDown={(e) => {
                    // Allow navigation and editing keys
                    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', ' '];
                    if (allowedKeys.includes(e.key)) return;

                    // Allow only letters (a-z, A-Z)
                    if (!/^[a-zA-Z]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Strip out any numbers and special characters, keep only letters and spaces
                    const value = e.currentTarget.value;
                    e.currentTarget.value = value.replace(/[^a-zA-Z\s]/g, '');
                  }}
                />
              </div>
              <div>
                <Input
                  label="Owner Email"
                  name="ownerEmail"
                  type="email"
                  disabled={!isEditable}
                />
              </div>
              <div>
                <Input
                  label="Owner Phone Number"
                  name="ownerPhoneNumber"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  disabled={!isEditable}
                  required
                  onKeyDown={(e) => {
                    // Allow navigation and editing keys
                    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                    if (allowedKeys.includes(e.key)) return;

                    // Allow only numbers
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    // Strip out any non-numeric characters and limit to 10 digits
                    const value = e.currentTarget.value.replace(/[^0-9]/g, '').slice(0, 10);
                    e.currentTarget.value = value;
                  }}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter 10 digits
                </p>
              </div>
              <div>
                <Select
                  label="Government ID Type"
                  name="governmentIDType"
                  disabled={!isEditable}
                  required
                >
                  <option value="">Select ID type</option>
                  <option value={GovernmentIDType.NationalID}>National ID</option>
                  <option value={GovernmentIDType.Passport}>Passport</option>
                  <option value={GovernmentIDType.DriversLicense}>Driver's License</option>
                </Select>
              </div>
              <div>
                <Input
                  label="Government ID Number"
                  name="governmentIDNumber"
                  disabled={!isEditable}
                  required
                />
              </div>
            </div>
          </div>

          {/* Section 3: Document Uploads */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-primary" />
              Document Uploads
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Government ID Document * {kycData.data && existingFiles?.governmentIDFile && <span className="text-xs text-green-600">(Uploaded)</span>}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="file"
                      name="governmentIDFile"
                      accept="image/*,.pdf"
                      disabled={!isEditable}
                      onChange={(e) => handleFileChange(e, 'governmentIDFile', true)}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-white
                        hover:file:bg-primary/90
                        disabled:opacity-50"
                    />
                    {fileValidationErrors.governmentIDFile && (
                      <p className="mt-1 text-xs text-red-600">{fileValidationErrors.governmentIDFile}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Upload image or PDF of government-issued ID (Max 10MB)
                    </p>
                  </div>
                  {getPreviewUrl('governmentIDFile', existingFiles?.governmentIDFile) && (
                    <div className="flex items-center justify-center">
                      <div className="relative w-full h-48 bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                        <img
                          src={getPreviewUrl('governmentIDFile', existingFiles?.governmentIDFile) || ''}
                          alt="Government ID Preview"
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                          <p className="text-xs text-white text-center">Preview</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Financial Statements * {kycData.data && existingFiles?.financialStatementFile && <span className="text-xs text-green-600">(Uploaded)</span>}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="file"
                      name="financialStatementFile"
                      accept="image/*,.pdf"
                      disabled={!isEditable}
                      onChange={(e) => handleFileChange(e, 'financialStatementFile', true)}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-white
                        hover:file:bg-primary/90
                        disabled:opacity-50"
                    />
                    {fileValidationErrors.financialStatementFile && (
                      <p className="mt-1 text-xs text-red-600">{fileValidationErrors.financialStatementFile}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Last 3 months financial statements (Max 10MB)
                    </p>
                  </div>
                  {getPreviewUrl('financialStatementFile', existingFiles?.financialStatementFile) && (
                    <div className="flex items-center justify-center">
                      <div className="relative w-full h-48 bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                        <img
                          src={getPreviewUrl('financialStatementFile', existingFiles?.financialStatementFile) || ''}
                          alt="Financial Statements Preview"
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                          <p className="text-xs text-white text-center">Preview</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Sales Records * {kycData.data && existingFiles?.salesRecordFile && <span className="text-xs text-green-600">(Uploaded)</span>}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="file"
                      name="salesRecordFile"
                      accept="image/*,.pdf"
                      disabled={!isEditable}
                      onChange={(e) => handleFileChange(e, 'salesRecordFile', true)}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-white
                        hover:file:bg-primary/90
                        disabled:opacity-50"
                    />
                    {fileValidationErrors.salesRecordFile && (
                      <p className="mt-1 text-xs text-red-600">{fileValidationErrors.salesRecordFile}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Last 3 months sales records (Max 10MB)
                    </p>
                  </div>
                  {getPreviewUrl('salesRecordFile', existingFiles?.salesRecordFile) && (
                    <div className="flex items-center justify-center">
                      <div className="relative w-full h-48 bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                        <img
                          src={getPreviewUrl('salesRecordFile', existingFiles?.salesRecordFile) || ''}
                          alt="Sales Records Preview"
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                          <p className="text-xs text-white text-center">Preview</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Premises Photos *
                </label>
                <div className="space-y-4">
                  {/* Photo 1 - Exterior */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">Photo 1 (Exterior)</label>
                      {kycData.data && existingFiles?.premisesPhoto1 && <span className="text-xs text-green-600">(Uploaded)</span>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="file"
                          name="premisesPhoto1"
                          accept="image/*"
                          disabled={!isEditable}
                          onChange={(e) => handleFileChange(e, 'premisesPhoto1', false)}
                          className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-primary file:text-white
                            hover:file:bg-primary/90
                            disabled:opacity-50"
                        />
                        {fileValidationErrors.premisesPhoto1 && (
                          <p className="mt-1 text-xs text-red-600">{fileValidationErrors.premisesPhoto1}</p>
                        )}
                      </div>
                      {getPreviewUrl('premisesPhoto1', existingFiles?.premisesPhoto1) && (
                        <div className="relative w-full h-32 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                          <img
                            src={getPreviewUrl('premisesPhoto1', existingFiles?.premisesPhoto1) || ''}
                            alt="Premises Photo 1 Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Photo 2 - Interior */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">Photo 2 (Interior)</label>
                      {kycData.data && existingFiles?.premisesPhoto2 && <span className="text-xs text-green-600">(Uploaded)</span>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="file"
                          name="premisesPhoto2"
                          accept="image/*"
                          disabled={!isEditable}
                          onChange={(e) => handleFileChange(e, 'premisesPhoto2', false)}
                          className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-primary file:text-white
                            hover:file:bg-primary/90
                            disabled:opacity-50"
                        />
                        {fileValidationErrors.premisesPhoto2 && (
                          <p className="mt-1 text-xs text-red-600">{fileValidationErrors.premisesPhoto2}</p>
                        )}
                      </div>
                      {getPreviewUrl('premisesPhoto2', existingFiles?.premisesPhoto2) && (
                        <div className="relative w-full h-32 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                          <img
                            src={getPreviewUrl('premisesPhoto2', existingFiles?.premisesPhoto2) || ''}
                            alt="Premises Photo 2 Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Photo 3 - Additional (Optional) */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">Photo 3 (Additional - Optional)</label>
                      {kycData.data && existingFiles?.premisesPhoto3 && <span className="text-xs text-green-600">(Uploaded)</span>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="file"
                          name="premisesPhoto3"
                          accept="image/*"
                          disabled={!isEditable}
                          onChange={(e) => handleFileChange(e, 'premisesPhoto3', false)}
                          className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-primary file:text-white
                            hover:file:bg-primary/90
                            disabled:opacity-50"
                        />
                        {fileValidationErrors.premisesPhoto3 && (
                          <p className="mt-1 text-xs text-red-600">{fileValidationErrors.premisesPhoto3}</p>
                        )}
                      </div>
                      {getPreviewUrl('premisesPhoto3', existingFiles?.premisesPhoto3) && (
                        <div className="relative w-full h-32 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                          <img
                            src={getPreviewUrl('premisesPhoto3', existingFiles?.premisesPhoto3) || ''}
                            alt="Premises Photo 3 Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  All photos must be images (Max 10MB each)
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditable && (
            <div className="p-6 bg-gray-50">
              {hasFileValidationErrors && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">
                    Please fix file validation errors before submitting.
                  </p>
                </div>
              )}

              {/* Form Error */}
              {formError && (
                <div className="mb-4">
                  <ErrorMessage
                    heading="Submission Error"
                    message={formError.message}
                  />
                </div>
              )}

              {/* Required Files Error */}
              {requiredFilesError && (
                <div className="mb-4">
                  <ErrorMessage
                    heading="Required Documents Missing"
                    message={requiredFilesError}
                  />
                </div>
              )}
              <div className="flex gap-4">
                <HighlightedButton
                  type="submit"
                  isSubmitting={state === 'submitting'}
                  disabled={hasFileValidationErrors}
                  onClick={(e) => {
                    // Only validate for initial submission (not updates)
                    if (!isBeingVerified && !isRejected && !hasAllRequiredFiles()) {
                      e.preventDefault();
                      const errorMessage = 'Please upload all required documents before submitting: Government ID, Financial Statements, Sales Records, and at least 2 Premises Photos.';
                      setRequiredFilesError(errorMessage);
                      toast.warning(errorMessage, {
                        position: "top-center",
                        autoClose: 7000,
                      });
                      // Removed scroll to top as per user request
                    } else {
                      setRequiredFilesError(null);
                    }
                  }}
                >
                  {isBeingVerified || isRejected ? 'Update KYC' : 'Submit KYC'}
                </HighlightedButton>
                {/* Show reset button only for first-time submission when no KYC has been submitted yet */}
                {kycData.status === KYCStatus.NotVerified && (
                  <Button type="reset">
                    Reset Form
                  </Button>
                )}
              </div>
            </div>
          )}
        </ValidatedForm>
      </div>
    </div>
  );
}
